import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Target, Cpu, Zap } from 'lucide-react';

export const AboutIntro: React.FC = () => {
  const { t } = useTranslation();
  const methodologySteps = t('about.methodology.steps', { returnObjects: true }) as any[];
  // Updated icons to match the darker/tech vibe
  const icons = [Target, Cpu, Zap];

  return (
    <section className="relative py-24 sm:py-32 overflow-hidden bg-slate-950 text-slate-200 selection:bg-cyan-500/30">
      {/* Abstract Background */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 pointer-events-none" />
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-cyan-900/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
          <motion.div 
            className="flex justify-center relative"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className="relative w-80 h-80 md:w-96 md:h-96">
               {/* Tech Circles around image */}
              <div className="absolute inset-0 rounded-full border border-cyan-500/20 animate-[spin_10s_linear_infinite]" />
              <div className="absolute -inset-4 rounded-full border border-dashed border-slate-700 animate-[spin_15s_linear_infinite_reverse]" />
              
              <img
                src="/images/Victor Lazari.webp"
                alt="Victor Lazari"
                className="rounded-full w-full h-full object-cover border-4 border-slate-900 shadow-2xl relative z-10 grayscale hover:grayscale-0 transition-all duration-500"
              />
            </div>
          </motion.div>

          <div className="text-center lg:text-left">
            <motion.h1 
              className="text-4xl md:text-6xl font-black text-white tracking-tight leading-none mb-6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              {t('about.intro.title')}<br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">{t('about.intro.titleHighlight')}</span>
            </motion.h1>
            <motion.p 
              className="text-lg md:text-xl text-slate-400 leading-relaxed"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {t('about.intro.subtitle')}
            </motion.p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
             <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{t('about.intro.methodTitle')}</h2>
             <div className="h-1 w-20 bg-cyan-500 mx-auto rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connection Line (Desktop) */}
            <div className="absolute top-12 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent hidden md:block" />

            {methodologySteps.map((step, index) => {
              const Icon = icons[index];
              return (
                <motion.div
                  key={index}
                  className="relative bg-slate-900 border border-slate-800 p-8 rounded-2xl hover:border-cyan-500/30 transition-all duration-300 group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.5, delay: 0.2 + (index * 0.1) }}
                >
                  <div className="relative z-10 flex justify-center mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-slate-950 border border-slate-800 flex items-center justify-center text-cyan-600 group-hover:text-cyan-400 group-hover:border-cyan-500/50 group-hover:scale-110 transition-all duration-300 shadow-lg">
                      {Icon && <Icon className="h-8 w-8" />}
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white text-center mb-3 group-hover:text-cyan-400 transition-colors">{step.name}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed text-center">{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
        
        <motion.div 
          className="mt-24 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="inline-block p-1 rounded-2xl bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600">
            <a
              href="/contact"
              className="block bg-slate-950 text-white font-bold px-10 py-4 rounded-xl hover:bg-slate-900 transition-all duration-300"
            >
              {t('about.cta.button')}
            </a>
          </div>
          <p className="mt-4 text-xs text-slate-500 font-mono uppercase tracking-widest">
             {t('about.intro.availability')}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutIntro;
